/**
 * The eight feature cards (design_handoff_fitvent_website_v8, section 3).
 *
 * Each card names a SURFACE COMPONENT rather than a screenshot. The captures
 * these replaced clipped badly across the card widths, went soft on retina,
 * carried whatever was in the database the morning they were taken (one shipped
 * a real organiser's postal address), and would have rotted the moment the
 * product moved. See surfaces.tsx.
 *
 * Body copy is quoted from the handoff. The TITLES have been shortened away
 * from it in four places: six of the eight used the same "clause, comma,
 * clause" rhythm, and that uniformity is what reads as machine-written, not
 * any single phrase. A person writing eight headlines produces uneven ones, so
 * these are now long, short, medium, long, long, short, short, short. Card 4
 * also lost "itself", which was a filler intensifier doing no work.
 *
 * They remain value statements rather than feature lists: every one names what
 * the organiser gets, not what the software has.
 *
 * `ribbon` is the card's ground. All eight are built from the same two brand
 * hues but each is anchored differently with its own sweep, so the grid does
 * not read as eight copies of one card. Varying the anchor is what does the
 * work; varying the colours would fight the brand.
 *
 * EVERY ANCHOR STAYS OUT OF THE TOP-LEFT, where the title and body sit. A
 * first pass anchored some of them there and the saturated middle of the
 * gradient ran straight under the body copy, which dropped it to roughly 2:1
 * against its ground. The ribbons are decoration; the words are the point, and
 * a card nobody can read is a worse card however good the gradient is.
 *
 * `titleWidth` is a measure, not a width: it stops a 28px title running the
 * full span of a four-column card, where the line gets too long to scan.
 */

import type { AnimId, BandId } from './animations';
import type { PointIcon } from './point-icons';
import type { SurfaceId } from './surfaces';

export type Feature = {
  title: string;
  body: string;
  /** Columns out of six. */
  span: number;
  /** Measure for the title and body. */
  titleWidth: number;
  ribbon: string;
  /** The product surface this card shows, bleeding off the bottom. */
  surface: SurfaceId;
  /** Phones sit on the ribbon itself rather than inside a white frame. */
  devices?: boolean;
  /** The live strip on the closed card. Card 1 has none: it runs a brand swap. */
  band?: BandId;
  /** Four ticked bullets, shown in the overlay header. Four to eight words, no full stops. */
  bullets: string[];
  /** Zone 2. A second product surface and an animation, NEVER the card's own surface. */
  overlaySurface?: SurfaceId;
  overlayImg?: { src: string; alt: string };
  overlayAnim?: AnimId;
  /** A second animation, where the card needs two halves of one argument. */
  overlaySecondAnim?: AnimId;
  /** Zone 2 stat. Only two cards have one. */
  stat?: { value: string; caption: string };
  /** Zone 3: three sentences, each with its own icon. No headings, no bullets. */
  points: Array<{ icon: PointIcon; text: string; soon?: boolean }>;
  /** Minimum frame height. The frame grows past this to reach the card's
   *  bottom edge; the number sets the card's height where copy is short. */
  frame: number;
};

/** Anchored gradient, so each card catches the light from a different corner. */
function ribbon(x: string, y: string, x2: string, y2: string): string {
  return [
    `radial-gradient(150% 120% at ${x} ${y}, rgba(42,223,223,0.95) 0%, rgba(42,223,223,0.55) 18%, rgba(0,109,109,0.42) 34%, rgba(30,58,95,0.16) 52%, rgba(255,255,255,0) 68%)`,
    `radial-gradient(120% 110% at ${x2} ${y2}, rgba(0,109,109,0.55) 0%, rgba(11,154,154,0.30) 26%, rgba(255,255,255,0) 56%)`,
    '#FFFFFF',
  ].join(', ');
}

/** Measure by span, per the handoff: >=4 -> 620, 3 -> 420, 2 -> 300. */
function measure(span: number): number {
  if (span >= 4) return 620;
  if (span === 3) return 420;
  return 300;
}

const RAW: Array<Omit<Feature, 'titleWidth' | 'ribbon'> & { anchor: [string, string, string, string] }> = [
  {
    title: 'Sell on your own brand and your own domain',
    body: 'Entry pages, checkout, wallet passes, live standings and the judging app all carry your colours and your logo on your own address. Athletes deal with you the whole way through.',
    span: 4,
    frame: 300,
    surface: 'brandedPages',
    bullets: ['Your logo, colours and typeface', 'Your own domain', 'Emails sent under your name', 'Branded standings and judging'],
    overlayAnim: 'branded',
    points: [
      { icon: 'globe', text: 'Point your domain at Fitvent and the certificate is handled for you.' },
      { icon: 'wallet', text: 'The wallet pass, the standings and the judging app carry your brand too.' },
      { icon: 'send', text: 'Emails go out under your name, so athletes recognise the sender.' },
    ],
    devices: true,
    anchor: ['106%', '96%', '-8%', '104%'],
  },
  {
    title: 'Take entries from anyone',
    body: 'Solo athletes, teams, squads and clubs all enter through the same checkout. Clubs get a co-branded page where members pay for themselves, and groups can be entered together in one go.',
    span: 2,
    frame: 300,
    surface: 'splitPayment',
    band: 'entries',
    bullets: ['Split payment across a team', 'Squads, pairs and relays', 'Co-branded club pages', 'Group entries in one pass'],
    overlayAnim: 'split',
    overlaySecondAnim: 'entrants',
    points: [
      { icon: 'users', text: 'One captain books and the team\u2019s place holds until the last share is paid.' },
      { icon: 'building', text: 'Clubs get their own co-branded page, with members paying individually.' },
      { icon: 'layers', text: 'Groups can be entered together and paid for in one pass.' },
    ],
    anchor: ['104%', '108%', '50%', '120%'],
  },
  {
    title: 'Automated waves, heats, draws and scoring',
    body: 'Build the competition from the entries you already hold, then score it live. Every event type has its own engine.',
    span: 3,
    frame: 300,
    surface: 'bracket',
    bullets: ['Waves, heats and lanes', 'Seeded draws and brackets', 'Pools and progression', 'Live standings in your brand'],
    overlayAnim: 'waves',
    stat: { value: '11 engines', caption: 'one competition engine per event type, plus build your own for anything else' },
    points: [
      { icon: 'shuffle', text: 'Late entries and withdrawals re-seed the schedule automatically.' },
      { icon: 'chart', text: 'Standings update all day for athletes and supporters.' },
      { icon: 'phone', text: 'Judges and officials score from their own phone.' },
    ],
    anchor: ['8%', '112%', '100%', '96%'],
  },
  {
    title: 'Run the day, from check-in to the last result',
    body: 'Check athletes in, roster your volunteers and officials automatically, and keep every record locked and exportable.',
    span: 3,
    frame: 430,
    surface: 'checkIn',
    band: 'checkin',
    bullets: ['Fast check-in on any phone', 'Smart volunteer scheduling', 'Officials and judges rostered', 'Waivers signed and stored'],
    overlayAnim: 'checkin',
    points: [
      { icon: 'scan', text: 'Scan a queue of athletes on any phone.' },
      { icon: 'calendar', text: 'Shifts are scheduled for you, with briefs and reminders sent automatically.' },
      { icon: 'shield', text: 'Waivers and screening are signed before anyone travels.' },
    ],
    anchor: ['100%', '100%', '112%', '40%'],
  },
  {
    title: 'Sell photos, merch and add-ons in the same basket',
    body: 'The strongest ancillary engine in fitness. Photos, merchandise, coaching, refund protection, transport and lodging all sell alongside the entry, lifting average revenue per athlete.',
    span: 2,
    frame: 400,
    surface: 'addOns',
    bullets: ['Photos and merchandise', 'Coaching and training plans', 'Transport and lodging', 'Refund protection'],
    overlaySurface: 'sponsors',
    overlayAnim: 'basket',
    points: [
      { icon: 'tag', text: 'You set the price, or take a commission where we supply.' },
      { icon: 'basket', text: 'Add-ons sell natively, in the same checkout as the entry.' },
      { icon: 'receipt', text: 'Everything an athlete bought sits with their entry.' },
    ],
    anchor: ['50%', '124%', '-10%', '104%'],
  },
  {
    title: 'Bring athletes back next season',
    body: 'Manage comms and campaigns from the same place as the entries. Automated emails run the season, campaigns bring past entrants back, and attribution shows which channel sold the entry.',
    span: 4,
    frame: 300,
    surface: 'campaigns',
    band: 'comms',
    bullets: ['Wave and heat assignments sent', 'Campaigns to past entrants', 'Segments from real entry data', 'Advertising ROI by channel'],
    overlayAnim: 'comms',
    overlaySecondAnim: 'branded',
    points: [
      { icon: 'send', text: 'Wave assignments, reminders and results go out on their own.' },
      { icon: 'filter', text: 'Segment by event, division or how many times they have entered.' },
      { icon: 'chart', text: 'Measure the return on every advertising campaign you run.' },
    ],
    anchor: ['-6%', '104%', '104%', '96%'],
  },
  {
    title: 'Money lands in your own account',
    body: 'Money lands in a Stripe account in your name and pays out rolling from the first entry. Entries, sponsors and commissions reconcile as they arrive.',
    span: 3,
    frame: 300,
    surface: 'money',
    band: 'payout',
    bullets: ['Rolling payouts, your account', 'One live ledger', 'Fees and refunds tracked', 'Free events cost nothing'],
    overlayAnim: 'payout',
    stat: { value: '6.5%', caption: 'per sale, all in, with no monthly fee and card processing included' },
    points: [
      { icon: 'bank', text: 'Payouts roll from the first entry sold.' },
      { icon: 'ledger', text: 'Entries, sponsors and commissions land in one ledger.' },
      { icon: 'refund', text: 'Refunds and transfers are handled in the same place.' },
    ],
    anchor: ['104%', '104%', '30%', '118%'],
  },
  {
    title: 'See what the day really cost',
    body: 'Supplier orders, sponsor deliverables and sponsor payments all sit against the event they belong to, so you can see the true cost and margin of a day before you run the next one.',
    span: 3,
    frame: 300,
    surface: 'sponsors',
    band: 'margin',
    bullets: ['Sponsor deliverables', 'Sponsor payment collected', 'Costs against the event', 'One ledger for the day'],
    overlaySurface: 'money',
    overlayAnim: 'margin',
    points: [
      { icon: 'truck', text: 'Supplier orders sit against the event that ordered them.', soon: true },
      { icon: 'handshake', text: 'Sponsors get what they paid for, tracked as deliverables.' },
      { icon: 'chart', text: 'See the true cost and margin of a day before you run the next one.' },
    ],
    anchor: ['-8%', '116%', '108%', '92%'],
  },
];

export const FEATURES: Feature[] = RAW.map((f) => ({
  title: f.title,
  body: f.body,
  span: f.span,
  frame: f.frame,
  surface: f.surface,
  devices: f.devices,
  band: f.band,
  bullets: f.bullets,
  overlaySurface: f.overlaySurface,
  overlayImg: f.overlayImg,
  overlayAnim: f.overlayAnim,
  overlaySecondAnim: f.overlaySecondAnim,
  stat: f.stat,
  points: f.points,
  titleWidth: measure(f.span),
  ribbon: ribbon(...f.anchor),
}));
